/**
 * Meter Values Chart Component
 * Displays a time-series chart showing cumulative energy consumption during a charging session
 * Shows: Total Energy consumed (cumulative), Current Power, and State of Charge
 */

import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Loader2 } from 'lucide-react';
import { MeterValueGridRow } from '../types/meter-value-types';
import { parseCurrentAmps } from '../utils/meter-value-utils';

// Nayax brand colors
const COLORS = {
  energy: '#FFCD00',     // Nayax Yellow - Cumulative energy
  power: '#619A09',      // Nayax Green - Current power
  soc: '#3B82F6',        // Blue - Battery State of Charge
  cost: '#9333EA',       // Purple - Cumulative cost
};

interface MeterValuesChartProps {
  data: MeterValueGridRow[];
  isLoading: boolean;
  error: string | null;
  /** 'A' plots current (A) instead of power (kW). Defaults to kW. */
  unitMode?: 'A' | 'kW';
}

interface ChartDataPoint {
  timestamp: string;
  time: string;              // Formatted time for X-axis display
  cumulativeEnergy: number;  // Total energy consumed up to this point (kWh)
  power: number | null;      // Power (kW), or current (A) when unitMode is 'A'
  stateOfCharge: number | null;
  cumulativeCost: number | null;  // Total cost up to this point
}

/**
 * Transform grid data to chart-friendly format
 * Calculates cumulative energy from delta values
 * Sorts by timestamp ascending for left-to-right display (session start to end)
 */
function transformDataForChart(data: MeterValueGridRow[], unitMode: 'A' | 'kW'): ChartDataPoint[] {
  // Sort by timestamp ascending (oldest first = session start on the left)
  const sorted = [...data].sort((a, b) => {
    return a.timestamp.localeCompare(b.timestamp);
  });

  let cumulativeEnergy = 0;
  let cumulativeCost = 0;

  return sorted.map((item) => {
    // Extract just the time portion for cleaner X-axis labels
    const timeParts = item.timestamp.split(' ');
    const time = timeParts.length > 1 ? timeParts[1] : item.timestamp;

    // Add delta energy to cumulative total
    if (item.deltaEnergy != null && item.deltaEnergy > 0) {
      cumulativeEnergy += item.deltaEnergy;
    }

    // Add cost to cumulative total (use costExclVat)
    if (item.costExclVat != null && item.costExclVat > 0) {
      cumulativeCost += item.costExclVat;
    }

    // Power might be in W (needs /1000 for kW) or already in kW
    // If power values are very large (>100), assume they are in W
    // If small (<100), assume already in kW
    let powerKw: number | null = null;
    if (item.power != null) {
      // Power > 100 likely means it's in Watts, convert to kW
      powerKw = item.power > 100 ? item.power / 1000 : item.power;
    }

    if (unitMode === 'A') {
      powerKw = parseCurrentAmps(item.current);
    }

    return {
      timestamp: item.timestamp,
      time,
      cumulativeEnergy: Number(cumulativeEnergy.toFixed(4)),
      power: powerKw != null ? Number(powerKw.toFixed(2)) : null,
      stateOfCharge: item.stateOfCharge,
      cumulativeCost: cumulativeCost > 0 ? Number(cumulativeCost.toFixed(2)) : null,
    };
  });
}

/**
 * Custom tooltip formatter - shows all values at hover point
 */
function CustomTooltip({ active, payload, label, unitMode = 'kW' }: {
  unitMode?: 'A' | 'kW';
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number | null;
    color: string;
    dataKey: string;
    payload?: ChartDataPoint;
  }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;

  // Find the full timestamp from the first payload item
  const fullTimestamp = payload[0]?.payload?.timestamp || label;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm min-w-[200px]">
      <p className="font-medium text-gray-900 mb-2 border-b pb-2">{fullTimestamp}</p>
      <div className="space-y-2">
        {payload.map((entry) => {
          if (entry.value == null) return null;

          let unit = '';
          let displayValue: string | number = entry.value;
          let displayName = entry.name;

          if (entry.dataKey === 'cumulativeEnergy') {
            unit = ' kWh';
            displayValue = Number(entry.value).toFixed(3);
            displayName = 'Total Energy';
          } else if (entry.dataKey === 'power') {
            unit = unitMode === 'A' ? ' A' : ' kW';
            displayValue = Number(entry.value).toFixed(1);
            displayName = unitMode === 'A' ? 'Current' : 'Power';
          } else if (entry.dataKey === 'stateOfCharge') {
            unit = '%';
            displayValue = Math.round(entry.value);
            displayName = 'Battery SoC';
          } else if (entry.dataKey === 'cumulativeCost') {
            unit = '';
            displayValue = Number(entry.value).toFixed(2);
            displayName = 'Cost';
          }

          return (
            <div key={entry.dataKey} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-sm flex-shrink-0"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-gray-600">{displayName}</span>
              </div>
              <span className="font-medium text-gray-900 tabular-nums">
                {displayValue}{unit}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function MeterValuesChart({ data, isLoading, error, unitMode = 'kW' }: MeterValuesChartProps) {
  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-80 bg-gray-50 rounded-lg">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <span className="text-gray-500">Loading chart data...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-80 bg-red-50 rounded-lg">
        <div className="text-center">
          <p className="text-red-600 font-medium">Failed to load chart data</p>
          <p className="text-red-500 text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data.length) {
    return (
      <div className="flex items-center justify-center h-80 bg-gray-50 rounded-lg">
        <div className="text-center">
          <p className="text-gray-500">No meter values to display</p>
          <p className="text-gray-400 text-sm mt-1">
            Chart will appear when data is available
          </p>
        </div>
      </div>
    );
  }

  const chartData = transformDataForChart(data, unitMode);

  // Check if we have SoC data (any non-null values > 0)
  const hasSocData = chartData.some((d) => d.stateOfCharge != null && d.stateOfCharge > 0);

  // Check if we have power data (allow 0 values, just not null)
  const hasPowerData = chartData.some((d) => d.power != null);

  // Check if we have cumulative energy data
  const hasEnergyData = chartData.some((d) => d.cumulativeEnergy > 0);

  // Check if we have cost data
  const hasCostData = chartData.some((d) => d.cumulativeCost != null && d.cumulativeCost > 0);

  // If no meaningful data, show a message
  if (!hasEnergyData && !hasPowerData && !hasSocData) {
    return (
      <div className="flex items-center justify-center h-80 bg-gray-50 rounded-lg">
        <div className="text-center">
          <p className="text-gray-500">No charging data available</p>
          <p className="text-gray-400 text-sm mt-1">
            Energy consumption data is not recorded for this session
          </p>
        </div>
      </div>
    );
  }

  // Calculate max values for Y-axis domains
  const maxEnergy = Math.max(...chartData.map(d => d.cumulativeEnergy));
  const maxPower = Math.max(...chartData.filter(d => d.power != null).map(d => d.power as number), 0);
  const maxCost = Math.max(...chartData.filter(d => d.cumulativeCost != null).map(d => d.cumulativeCost as number), 0);

  return (
    <div className="w-full h-80">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={chartData}
          margin={{ top: 10, right: hasCostData ? 110 : (hasSocData ? 70 : 60), left: 10, bottom: 10 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />

          {/* X-Axis: Time */}
          <XAxis
            dataKey="time"
            tick={{ fontSize: 11, fill: '#6B7280' }}
            tickLine={{ stroke: '#D1D5DB' }}
            axisLine={{ stroke: '#D1D5DB' }}
            interval="preserveStartEnd"
          />

          {/* Y-Axis Left: Cumulative Energy (kWh) */}
          <YAxis
            yAxisId="energy"
            orientation="left"
            domain={[0, Math.ceil(maxEnergy * 1.1 * 10) / 10]} // Add 10% padding
            tick={{ fontSize: 11, fill: '#6B7280' }}
            tickLine={{ stroke: '#D1D5DB' }}
            axisLine={{ stroke: '#D1D5DB' }}
            label={{
              value: 'Energy (kWh)',
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle', fill: '#6B7280', fontSize: 12 },
            }}
          />

          {/* Y-Axis Right: Power (kW) */}
          {hasPowerData && (
            <YAxis
              yAxisId="power"
              orientation="right"
              domain={[0, Math.ceil(maxPower * 1.1)]}
              tick={{ fontSize: 11, fill: '#6B7280' }}
              tickLine={{ stroke: '#D1D5DB' }}
              axisLine={{ stroke: '#D1D5DB' }}
              label={{
                value: unitMode === 'A' ? 'Current (A)' : 'Power (kW)',
                angle: 90,
                position: 'insideRight',
                style: { textAnchor: 'middle', fill: '#6B7280', fontSize: 12 },
              }}
            />
          )}

          {/* Y-Axis Far Right: SoC (%) - only if we have SoC data */}
          {hasSocData && (
            <YAxis
              yAxisId="soc"
              orientation="right"
              domain={[0, 100]}
              tick={{ fontSize: 11, fill: '#3B82F6' }}
              tickLine={{ stroke: '#3B82F6' }}
              axisLine={{ stroke: '#3B82F6' }}
              label={{
                value: 'SoC (%)',
                angle: 90,
                position: 'outsideRight',
                offset: hasPowerData ? 50 : 10,
                style: { textAnchor: 'middle', fill: '#3B82F6', fontSize: 12 },
              }}
            />
          )}

          {/* Y-Axis Far Right: Cost - only if we have cost data */}
          {hasCostData && (
            <YAxis
              yAxisId="cost"
              orientation="right"
              domain={[0, Math.ceil(maxCost * 1.1 * 100) / 100]}
              tick={{ fontSize: 11, fill: '#9333EA' }}
              tickLine={{ stroke: '#9333EA' }}
              axisLine={{ stroke: '#9333EA' }}
              label={{
                value: 'Cost',
                angle: 90,
                position: 'outsideRight',
                offset: hasSocData ? 90 : (hasPowerData ? 50 : 10),
                style: { textAnchor: 'middle', fill: '#9333EA', fontSize: 12 },
              }}
            />
          )}

          <Tooltip content={<CustomTooltip unitMode={unitMode} />} />

          <Legend
            wrapperStyle={{ paddingTop: '10px' }}
            formatter={(value) => (
              <span className="text-gray-700 text-sm">{value}</span>
            )}
          />

          {/* Cumulative Energy Area Chart - shows total energy consumed over time */}
          {hasEnergyData && (
            <Area
              yAxisId="energy"
              type="monotone"
              dataKey="cumulativeEnergy"
              name="Total Energy"
              stroke={COLORS.energy}
              fill={COLORS.energy}
              fillOpacity={0.3}
              strokeWidth={2}
              dot={false}
              connectNulls
            />
          )}

          {/* Power Line Chart - shows current charging power */}
          {hasPowerData && (
            <Line
              yAxisId="power"
              type="stepAfter"
              dataKey="power"
              name={unitMode === 'A' ? 'Current' : 'Power'}
              stroke={COLORS.power}
              strokeWidth={2}
              dot={false}
              connectNulls
            />
          )}

          {/* State of Charge Line Chart - battery level over time */}
          {hasSocData && (
            <Line
              yAxisId="soc"
              type="monotone"
              dataKey="stateOfCharge"
              name="Battery SoC"
              stroke={COLORS.soc}
              strokeWidth={2}
              dot={{ fill: COLORS.soc, strokeWidth: 0, r: 3 }}
              connectNulls
            />
          )}

          {/* Cumulative Cost Line Chart - total cost over time */}
          {hasCostData && (
            <Line
              yAxisId="cost"
              type="monotone"
              dataKey="cumulativeCost"
              name="Cost"
              stroke={COLORS.cost}
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              connectNulls
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
